/**
 * Equipo de agentes de la organizacion activa.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { TeamMemberView } from '@shared/api-types.ts';
import { MEMBER_ROLE_LABELS } from '@domain/types.ts';
import { api, errorMessage, isAbortError } from '../api.ts';
import { formatDateTime } from '../format.ts';
import { Chip, EmptyState, ErrorBanner, SkeletonRows } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type State =
  | { status: 'loading' }
  | { status: 'ready'; members: TeamMemberView[] }
  | { status: 'error'; message: string };

export function TeamScreen(): ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .listTeam(controller.signal)
      .then((members) => setState({ status: 'ready', members }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [reloadToken]);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Equipo</h1>
          <p className="page__subtitle">
            Personas con acceso a la bandeja de esta organizacion y el rol que tienen asignado.
          </p>
        </div>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          <Icon name="refresh" />
          Actualizar
        </button>
      </header>

      {state.status === 'loading' ? <SkeletonRows rows={5} /> : null}

      {state.status === 'error' ? (
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      ) : null}

      {state.status === 'ready' && state.members.length === 0 ? (
        <EmptyState
          icon="team"
          title="Todavia no hay agentes"
          description="Cuando se invite a alguien al equipo aparecera en esta lista con su rol."
        />
      ) : null}

      {state.status === 'ready' && state.members.length > 0 ? (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Ultimo acceso</th>
              </tr>
            </thead>
            <tbody>
              {state.members.map((member) => (
                <tr key={member.userId}>
                  <td className="table__strong">{member.name}</td>
                  <td>{member.email}</td>
                  <td>
                    <Chip tone={member.role === 'owner' ? 'accent' : member.role === 'admin' ? 'info' : 'neutral'}>
                      {MEMBER_ROLE_LABELS[member.role]}
                    </Chip>
                  </td>
                  <td>
                    <Chip tone={member.isActive ? 'success' : 'neutral'}>
                      {member.isActive ? 'Activo' : 'Inactivo'}
                    </Chip>
                  </td>
                  <td>{member.lastLoginAt ? formatDateTime(member.lastLoginAt) : 'Nunca'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
